import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, requestIdleCallback } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';
import { getSearchIntents, fillSearchAside, expandSearchQuery } from '@theme/custom-phaser-search-intent';

/** Phaser: products in the search box grid (three rows of five on desktop). */
const MAX_PRODUCTS = 15;

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Phaser: "Shop by spec" chips for the term they were worked out for.
   * @type {{ term: string, intents: import('@theme/custom-phaser-search-intent').ResolvedIntent[] } | null}
   */
  #intents = null;

  /** Phaser: the term whose results are on screen. */
  #renderedTerm = '';

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    this.querySelector('form')?.addEventListener('submit', this.#handleFormSubmit, { signal });

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });
      // Phaser: the side column follows the department picked since the last open.
      dialog.addEventListener(
        DialogOpenEvent.eventName,
        () => this.#fillIntents(this.refs.predictiveSearchResults, this.#renderedTerm),
        { signal }
      );

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    // Prefetch empty state (collection + recently viewed) so the modal is never input-only.
    requestIdleCallback(() => {
      this.#loadEmptyState();
    });
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      // Phaser: skip hidden chips (a category chip a spec chip covers, or a side-column list
      // that only shows on desktop).
      .filter((item) => item instanceof HTMLElement && !item.closest('[hidden]') && item.getClientRects().length > 0);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  /** Phaser: unique ids for the result links aria-activedescendant points at. */
  #activeLinkId = 0;

  // Phaser (a11y): the active result is tracked with data-active, not aria-selected. The results
  // are lists and headings, not a listbox of options, so aria-selected was invalid there. The
  // combobox announces the active result's link through aria-activedescendant instead.
  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.dataset.active === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) {
      this.refs.searchInput?.removeAttribute('aria-activedescendant');
      return;
    }

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.dataset.active = 'true';
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        delete item.dataset.active;
      }
    }

    const activeLink = activeItem?.querySelector('a');
    if (activeLink) {
      if (!activeLink.id) activeLink.id = `predictive-search-result-${++this.#activeLinkId}`;
      this.refs.searchInput.setAttribute('aria-activedescendant', activeLink.id);
    } else {
      this.refs.searchInput.removeAttribute('aria-activedescendant');
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    // Horizontal arrows fall through to native text-caret movement, which the
    // browser already mirrors in RTL.
    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        // Phaser: a chip picked with the arrow keys wins over the single-result shortcut.
        if (
          this.#currentIndex < 0 &&
          singleResultContainer instanceof HTMLElement &&
          singleResultContainer.dataset.singleResultUrl
        ) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          event.preventDefault();
          window.location.href = this.#searchPageUrl();
        }
        break;
      }
    }
  };

  /**
   * Phaser: the full results page URL, with shorthand expanded the same way as the dropdown
   * ("fc" opens the results for "flight controller"). Used by Enter and the View all button.
   * @returns {string}
   */
  #searchPageUrl() {
    const searchUrl = new URL(Theme.routes.search_url, location.origin);
    searchUrl.searchParams.set('q', expandSearchQuery(this.refs.searchInput.value).page);
    // Sold-out products last, as in the dropdown. Only one options[...] key: with two (prefix and
    // unavailable_products) Shopify drops both and its page links serialise them as a hash.
    searchUrl.searchParams.set('options[unavailable_products]', 'last');
    return searchUrl.toString();
  }

  /**
   * Phaser: View all (and any other submit of the search form) opens the expanded results page.
   * @param {SubmitEvent} event
   */
  #handleFormSubmit = (event) => {
    if (!this.refs.searchInput.value.trim()) return;
    event.preventDefault();
    window.location.href = this.#searchPageUrl();
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    // Phaser: shorthand is expanded first ("fc" -> "flight controller"); a group with two good
    // forms ("gnb" / "gaoneng") searches both and merges the products (snippets/phaser-search-synonyms).
    const { queries } = expandSearchQuery(searchTerm);
    const urls = queries.map((query) => {
      const url = new URL(Theme.routes.predictive_search_url, location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[limit_scope]', 'each');
      // Phaser: variant names (colours like "Muted Green"), SKUs and barcodes match; body text is left
      // out because long descriptions mention many unrelated products. Sold-out products rank last.
      url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag,variants.sku,variants.barcode');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      return url;
    });

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    // Phaser: spec chips load beside the results and fill in when ready (products never wait).
    this.#loadIntents(searchTerm, abortController.signal);

    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    const sectionId = this.dataset.sectionId;
    Promise.all(urls.map((url) => sectionRenderer.getSectionHTML(sectionId, false, url)))
      .then(([resultsMarkup, extraMarkup]) => {
        if (!resultsMarkup) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        if (abortController.signal.aborted) {
          deferredPromise.reject(new Error('Fetch aborted by user'));
          return;
        }

        let resultsTree = new DOMParser().parseFromString(resultsMarkup, 'text/html').body.firstChild;
        if (extraMarkup) {
          const extraTree = new DOMParser().parseFromString(extraMarkup, 'text/html').body.firstChild;
          resultsTree = this.#mergeProductResults(resultsTree, extraTree);
        }
        if (!resultsTree) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        this.#fillIntents(resultsTree, searchTerm);
        morph(predictiveSearchResults, resultsTree);
        this.#renderedTerm = searchTerm;
        this.#loadMoreProducts(searchTerm, queries[0], abortController.signal);

        this.#resetScrollPositions();

        // Count all result items (products, collections, pages, articles, queries)
        const resultCount = predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length;
        deferredPromise.resolve({ totalCount: resultCount });
      })
      .catch((error) => {
        deferredPromise.reject(error);
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Phaser: work out the "Shop by spec" chips for a term, then show them if its results are on screen.
   * @param {string} searchTerm
   * @param {AbortSignal} signal - aborted when a newer search starts
   */
  async #loadIntents(searchTerm, signal) {
    if (this.#intents?.term === searchTerm) return;

    let intents = [];
    try {
      intents = await getSearchIntents(searchTerm, signal);
    } catch {
      intents = [];
    }
    if (signal.aborted) return;

    this.#intents = { term: searchTerm, intents };
    if (this.#renderedTerm === searchTerm) {
      this.#fillIntents(this.refs.predictiveSearchResults, searchTerm);
    }
  }

  /**
   * Phaser: predictive search returns at most 10 products. When it filled all 10, top the grid
   * up to 15 (three rows of five on desktop) from the full product search, rendered by this same
   * section, without repeats. Runs after the first 10 are on screen, so typing never waits for it.
   * @param {string} searchTerm - what the shopper typed
   * @param {string} query - the query sent (shorthand expanded)
   * @param {AbortSignal} signal - aborted when a newer search starts
   */
  async #loadMoreProducts(searchTerm, query, signal) {
    const { predictiveSearchResults } = this.refs;
    const list = predictiveSearchResults.querySelector('.phaser-search-products');
    if (!list || list.children.length < 10 || list.children.length >= MAX_PRODUCTS || !this.dataset.sectionId) return;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[unavailable_products]', 'last');

    let markup = '';
    try {
      markup = await sectionRenderer.getSectionHTML(this.dataset.sectionId, true, url, signal);
    } catch {
      return;
    }
    if (signal.aborted || this.#renderedTerm !== searchTerm) return;

    const extra = new DOMParser().parseFromString(markup, 'text/html').querySelector('.phaser-search-products');
    const current = predictiveSearchResults.querySelector('.phaser-search-products');
    if (!extra || !current) return;

    const key = (/** @type {Element} */ card) => (card.querySelector('a[href*="/products/"]')?.getAttribute('href') || '').split('?')[0];
    const seen = new Set([...current.children].map(key));
    for (const card of [...extra.children]) {
      if (current.children.length >= MAX_PRODUCTS) break;
      const k = key(card);
      if (!k || seen.has(k)) continue;
      seen.add(k);
      current.append(card);
    }
  }

  /**
   * Phaser: add the second search form's products after the first's, without repeats, keeping
   * Shopify's order in each (up to 15, three rows of five on desktop). If the first form found no
   * products, the second form's results are used as they are.
   * @param {ChildNode | null} first
   * @param {ChildNode | null} second
   * @returns {ChildNode | null}
   */
  #mergeProductResults(first, second) {
    if (!(second instanceof Element)) return first;
    if (!(first instanceof Element)) return second;
    const listSelector = '.predictive-search-results__wrapper-products';
    const firstList = first.querySelector(listSelector);
    const secondList = second.querySelector(listSelector);
    if (!secondList) return first;
    if (!firstList) return second;

    const key = (/** @type {Element} */ card) => {
      const href = card.querySelector('a[href*="/products/"]')?.getAttribute('href') || '';
      return href.split('?')[0];
    };
    const seen = new Set([...firstList.children].map(key));
    for (const card of [...secondList.children]) {
      if (firstList.children.length >= MAX_PRODUCTS) break;
      const k = key(card);
      if (!k || seen.has(k)) continue;
      seen.add(k);
      firstList.append(card);
    }
    // A merged list is never a "single result" shortcut.
    if (firstList.children.length > 1) first.querySelector('[data-single-result-url]')?.remove();
    return first;
  }

  /**
   * Phaser: fill the side column (snippets/phaser-search-aside.liquid) for a term: spec links,
   * categories, or popular / recently viewed collections.
   * @param {ParentNode} container
   * @param {string} searchTerm - '' for the empty box
   */
  #fillIntents(container, searchTerm) {
    const intents = searchTerm && this.#intents?.term === searchTerm ? this.#intents.intents : [];
    fillSearchAside(container, intents);
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#renderedTerm = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    /* The empty state is page-independent — it renders a collection grid plus
       recently-viewed, identical everywhere. It only uses the current URL
       because that is the default.

       On an app-proxy path (/apps/*) that default is expensive: Shopify has to
       go back out to the app's server before it can render and extract the
       section. Measured on CCC with a byte-identical 21.6KB section,
       interleaved, 5 samples, cache-busted, TTFB 4ms on both sides:
         /collections/<x>?section_id=…   body  174-249 ms
         /apps/iwish?section_id=…        body  609-1390 ms
       So on proxy paths only, render it against the site root instead. Same
       markup, none of the proxy round-trip. */
    const isAppProxy = window.location.pathname.startsWith('/apps/');
    const url = new URL(isAppProxy ? '/' : window.location.href, window.location.origin);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      try {
        const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
        if (recentlyViewedMarkup) {
          const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
          const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
          const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');

          if (recentlyViewedProductsHtml && collectionElement) {
            for (const child of recentlyViewedProductsHtml.children) {
              if (child instanceof HTMLElement) {
                child.setAttribute('ref', 'recentlyViewedWrapper');
              }
            }
            collectionElement.prepend(...recentlyViewedProductsHtml.children);
          } else if (recentlyViewedProductsHtml && !collectionElement) {
            // Empty-state collection has no product grid; still show recently viewed.
            // Phaser: products go in the main column, beside the side column.
            const inner =
              parsedEmptySectionMarkup.querySelector('.phaser-search-main') ||
              parsedEmptySectionMarkup.querySelector('.predictive-search-results__inner');
            const noResults = parsedEmptySectionMarkup.querySelector('.predictive-search-results__no-results');
            noResults?.remove();
            for (const child of recentlyViewedProductsHtml.children) {
              if (child instanceof HTMLElement) {
                child.setAttribute('ref', 'recentlyViewedWrapper');
              }
            }
            (inner ?? parsedEmptySectionMarkup).prepend(...recentlyViewedProductsHtml.children);
          }
        }
      } catch {
        // Keep empty-state collection markup if recently viewed merge fails.
      }
    }

    if (abortController.signal.aborted) return;

    this.#fillIntents(parsedEmptySectionMarkup, '');
    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
